'use client';

import type { ReactNode } from 'react';
import React, { useMemo, useRef } from 'react';
import clsx from 'clsx';
import { Text } from 'components/Text';
import { createDescendantsContext } from 'hooks/useDescendants';
import { getMetaTextColor } from 'theme-utils';
import { mergeRefs } from '@react-aria/utils';
import { IconX } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import styles from './Chip.module.css';
import { ChipCloseButton } from './ChipCloseButton';
import type { ChipColors, ChipProps } from './types';

// -----------------------------------------------------------------------------
// Chip Descendants Context
// -----------------------------------------------------------------------------
// Used by TokenizedField to track chip refs for keyboard navigation.
// Chips automatically register when inside a ChipDescendantsProvider.

export const {
  DescendantsContext: ChipDescendantsContext,
  DescendantsProvider: ChipDescendantsProvider,
  useDescendant: useChipDescendant,
} = createDescendantsContext();

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type IconProps = { size?: number; 'aria-hidden'?: boolean };

// -----------------------------------------------------------------------------
// Helper functions
// -----------------------------------------------------------------------------

function getDeleteLabel(children: ReactNode, deleteAriaLabel?: string): string {
  if (deleteAriaLabel) {
    return deleteAriaLabel;
  }
  if (typeof children === 'string') {
    return `Delete ${children}`;
  }
  return 'Delete';
}

/**
 * Get the color tokens for a given meta color.
 * These are passed as CSS variables and CSS handles the conditional logic.
 */
function getColorTokens(color: ChipColors): React.CSSProperties {
  return {
    '--vapor-chip-color-low': theme.colors.meta.lowOpacity[color],
    '--vapor-chip-color-hover':
      theme.colors.meta.lowOpacity.interactive.hover[color],
    '--vapor-chip-color-active':
      theme.colors.meta.lowOpacity.interactive.active[color],
    '--vapor-chip-color-opaque': theme.colors.meta.opaque[color],
    '--vapor-chip-color-medium': theme.colors.meta.mediumOpacity[color],
    '--vapor-chip-color-medium-hover':
      theme.colors.meta.mediumOpacity.interactive.hover[color],
    '--vapor-chip-text': getMetaTextColor(color),
  } as React.CSSProperties;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function Chip({
  avatar,
  selectable,
  clickable,
  as,
  iconBefore: iconBeforeProp,
  iconAfter: iconAfterProp,
  onDelete: onDeleteProp,
  onClick: onClickProp,
  children,
  deleteAriaLabel,
  color = 'default',
  disabled,
  id,
  highlight,
  enableTooltip = true,
  variant = 'fill',
  size = 'medium',
  className,
  style,
  ref: forwardedRef,
  ...other
}: ChipProps) {
  // Support deprecated `clickable` prop with fallback
  const isSelectable = selectable ?? clickable ?? false;
  const deleteIconRef = useRef<HTMLElement>(null);
  const chipRef = useRef<HTMLElement>(null);
  const ref = useMemo(() => mergeRefs(forwardedRef, chipRef), [forwardedRef]);

  // Register with parent context (e.g., TokenizedField) for keyboard navigation
  // Cast needed because useRef initializes with null but useDescendant expects non-nullable
  useChipDescendant(id ?? '', chipRef as React.RefObject<HTMLElement>);

  // Render avatar or icon before content
  const iconBefore = useMemo(() => {
    if (avatar && React.isValidElement<IconProps>(avatar)) {
      return React.cloneElement(avatar, {
        size: size === 'medium' ? 20 : 16,
        'aria-hidden': true,
      });
    }
    if (iconBeforeProp && React.isValidElement<IconProps>(iconBeforeProp)) {
      return React.cloneElement(iconBeforeProp, {
        size: 16,
        'aria-hidden': true,
      });
    }
    return null;
  }, [avatar, iconBeforeProp, size]);

  // Render icon after content (custom iconAfter takes precedence over delete icon)
  const iconAfter = useMemo(() => {
    // Custom iconAfter takes precedence
    if (iconAfterProp && React.isValidElement<IconProps>(iconAfterProp)) {
      return React.cloneElement(iconAfterProp, {
        size: 16,
        'aria-hidden': true,
      });
    }

    // Fall back to delete icon if onDelete is provided
    if (!onDeleteProp) return null;

    if (!isSelectable) {
      // Use currentColor to inherit from chip (CSS handles focus state colors)
      return (
        <ChipCloseButton
          aria-label={getDeleteLabel(children, deleteAriaLabel)}
          color="currentColor"
          isDisabled={disabled}
          onPress={() => onDeleteProp()}
        />
      );
    }

    return (
      <IconX
        ref={deleteIconRef}
        data-testid="chip-iconafter-box"
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        size={16}
        style={{ cursor: 'pointer' }}
      />
    );
  }, [
    iconAfterProp,
    onDeleteProp,
    isSelectable,
    children,
    deleteAriaLabel,
    disabled,
  ]);

  // Handle click events
  function handleClick(
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
  ) {
    const target = e.target as HTMLElement;
    if (deleteIconRef.current?.contains(target)) {
      e.stopPropagation();
      onDeleteProp?.();
      return;
    }

    // Focus the chip if selectable (for visual "selected" state)
    if (isSelectable) {
      chipRef.current?.focus();
    }

    e.stopPropagation();
    onClickProp?.(e);
  }

  // CSS variables: color tokens
  const cssVars: React.CSSProperties = {
    ...getColorTokens(color),
    ...style,
  };

  // Determine element type:
  // 1. Explicit `as` prop takes precedence
  // 2. Auto-detect trigger from aria attributes (Menu, Popover, etc.)
  // 3. Fall back to selectable-based logic
  const isTrigger = 'aria-haspopup' in other || 'aria-expanded' in other;
  const Component = as ?? (isSelectable || isTrigger ? 'button' : 'div');

  return (
    <Component
      ref={ref as React.Ref<HTMLButtonElement & HTMLDivElement>}
      className={clsx(styles.chip, className)}
      data-has-avatar={avatar ? true : undefined}
      data-has-delete={onDeleteProp ? true : undefined}
      data-has-icon-after={iconAfterProp ? true : undefined}
      data-has-icon-before={iconBefore ? true : undefined}
      data-selectable={isSelectable || undefined}
      data-size={size}
      data-testid="styled-chip"
      data-variant={variant}
      disabled={disabled || false}
      onClick={handleClick}
      style={cssVars}
      {...other}
    >
      {iconBefore}
      <Text
        color="inherit"
        enableTooltip={enableTooltip && typeof children === 'string'}
        fontWeight="medium"
        highlight={highlight}
        truncate
        variant="caption"
      >
        {children}
      </Text>
      {iconAfter}
    </Component>
  );
}
